// Cross-platform token storage abstraction
// React Native (Expo): uses SecureStore if available; fallback to in-memory.
// Web: uses localStorage.

interface TokenStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

class WebStorage implements TokenStorage {
  async getItem(key: string) { try { return localStorage.getItem(key); } catch { return null; } }
  async setItem(key: string, value: string) { try { localStorage.setItem(key, value); } catch { /* ignore */ } }
  async removeItem(key: string) { try { localStorage.removeItem(key); } catch { /* ignore */ } }
}

class MemoryStorage implements TokenStorage {
  private store = new Map<string, string>();
  async getItem(key: string) { return this.store.get(key) || null; }
  async setItem(key: string, value: string) { this.store.set(key, value); }
  async removeItem(key: string) { this.store.delete(key); }
}

class SecureNativeStorage implements TokenStorage {
  private memoryFallback = new MemoryStorage();
  private secure: any; // Expo SecureStore or similar injected global
  constructor() {
    // Detect Expo SecureStore
    // @ts-ignore
    if (typeof SecureStore !== 'undefined') {
      // @ts-ignore
      this.secure = SecureStore;
    } else if (typeof global !== 'undefined' && (global as any).SecureStore) {
      this.secure = (global as any).SecureStore;
    }
  }
  async getItem(key: string) {
    if (this.secure?.getItemAsync) return this.secure.getItemAsync(key);
    return this.memoryFallback.getItem(key);
  }
  async setItem(key: string, value: string) {
    if (this.secure?.setItemAsync) return this.secure.setItemAsync(key, value);
    return this.memoryFallback.setItem(key, value);
  }
  async removeItem(key: string) {
    if (this.secure?.deleteItemAsync) return this.secure.deleteItemAsync(key);
    return this.memoryFallback.removeItem(key);
  }
}

let impl: TokenStorage;
if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') impl = new WebStorage();
else impl = new SecureNativeStorage();

export const tokenStorage: TokenStorage = impl;

export const ACCESS_KEY = 'auth.access';
export const REFRESH_KEY = 'auth.refresh';
